import type * as React from "react"
import { cn } from "../lib/utils"
import { Card, CardContent } from "./ui/card"

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  description?: string
  trend?: {
    value: number
    label: string
  }
  variant?: "default" | "primary" | "success" | "warning" | "destructive"
  className?: string
  testId?: string
  onClick?: () => void
}

const variantStyles = {
  default: "bg-gradient-to-br from-muted/50 to-muted/30 text-muted-foreground shadow-sm",
  primary: "bg-gradient-to-br from-primary/30 to-primary/20 text-blue-300 shadow-lg shadow-primary/30",
  success: "bg-gradient-to-br from-emerald-500/30 to-emerald-500/20 text-emerald-300 shadow-lg shadow-emerald-500/30",
  warning: "bg-gradient-to-br from-yellow-400/30 to-yellow-500/20 text-yellow-300 shadow-lg shadow-yellow-400/30",
  destructive: "bg-gradient-to-br from-red-500/30 to-red-500/20 text-red-300 shadow-lg shadow-red-500/30",
}

export function StatCard({ title, value, icon, description, trend, variant = "default", className, testId, onClick }: StatCardProps) {
  return (
    <Card
      className={cn(
        "overflow-hidden",
        onClick && "cursor-pointer hover:shadow-md transition-shadow",
        className
      )}
      data-testid={testId}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground" data-testid={testId ? `${testId}-title` : undefined}>{title}</p>
            <div className={cn("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg", variantStyles[variant])}>
              {icon}
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-3xl font-bold tracking-tight" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            {trend && (
              <p className={cn("text-xs font-medium", trend.value >= 0 ? "text-emerald-600" : "text-destructive")}>
                {trend.value >= 0 ? "+" : ""}
                {trend.value}% {trend.label}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
